// Quem entrou (ex.: com o Google) e ainda não tem conta cria a sua aqui: negócio ou agência, com teste grátis.
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { Briefcase, Store } from 'lucide-react'
import { TRIAL_DAYS, type AccountKind } from '@impulso/shared'
import type { CreateAccountResponse } from '@impulso/shared/api/identity'
import { api, errorMessage } from '../../lib/api'
import { useSession } from '../../lib/session'
import AuthShell, { FormError } from '../../features/auth/AuthShell'
import { safeNext } from '../../features/auth/session-helpers'
import { Field } from '../../ui'

export default function CreateAccount() {
  const navigate = useNavigate()
  const { me, refresh } = useSession()
  const [kind, setKind] = useState<AccountKind>('business')
  const [name, setName] = useState('')
  const [terms, setTerms] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (me && me.accounts.length > 0 && !busy) return <Navigate to="/app" replace />

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!terms) return setError('Para continuar, aceite os termos de uso e a política de privacidade.')
    setBusy(true)
    try {
      const r = await api.post<CreateAccountResponse>('/accounts', { accountKind: kind, name, acceptTerms: true })
      await refresh()
      navigate(safeNext(r.next), { replace: true })
    } catch (err) {
      setError(errorMessage(err))
      setBusy(false)
    }
  }

  return (
    <AuthShell wide title="Crie sua conta" sub={`Falta só isto para começar: ${TRIAL_DAYS} dias grátis, sem cartão.`} footer={<>Entrou com o e-mail errado? <Link className="strong" style={{ color: 'var(--primary)' }} to="/sair">Sair</Link></>}>
      <form className="stack" style={{ gap: 14 }} onSubmit={submit}>
        <FormError error={error} />
        <div className="grid g-2" style={{ gap: 10 }} role="radiogroup" aria-label="Tipo de conta">
          <button type="button" role="radio" aria-label="Sou um negócio" aria-checked={kind === 'business'} className={`option-card ${kind === 'business' ? 'on' : ''}`} onClick={() => setKind('business')} style={{ padding: 14 }}>
            <span className="ic purple"><Store size={18} /></span>
            <span><span className="strong">Sou um negócio</span><br /><span className="small muted">Quero anunciar o meu</span></span>
            <span className="radio" />
          </button>
          <button type="button" role="radio" aria-label="Sou agência" aria-checked={kind === 'agency'} className={`option-card ${kind === 'agency' ? 'on' : ''}`} onClick={() => setKind('agency')} style={{ padding: 14 }}>
            <span className="ic blue"><Briefcase size={18} /></span>
            <span><span className="strong">Sou agência</span><br /><span className="small muted">Cuido de vários clientes</span></span>
            <span className="radio" />
          </button>
        </div>
        <Field label={kind === 'business' ? 'Nome do seu negócio' : 'Nome da agência'}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === 'business' ? 'Ex.: Doce Ateliê' : 'Ex.: Júlia Marketing'} required minLength={2} autoFocus />
        </Field>
        <label className="row small" style={{ alignItems: 'flex-start', gap: 8 }}>
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} style={{ marginTop: 3 }} />
          <span>Li e aceito os <Link to="/termos" target="_blank" className="strong" style={{ color: 'var(--primary)' }}>termos de uso</Link> e a <Link to="/privacidade" target="_blank" className="strong" style={{ color: 'var(--primary)' }}>política de privacidade</Link>.</span>
        </label>
        <button className="btn primary lg block" disabled={busy}>{busy ? 'Criando sua conta…' : 'Começar o teste grátis'}</button>
      </form>
    </AuthShell>
  )
}
